import { GetNewGuid } from "../../helpers/GuidHelper";
import { Actor, normalizeActor } from "../Actor";
import { Variable, VariableScope, VariableType } from "./Variable";

export const BUILT_IN_VARIABLE_NAMES = [
  "InternalEmergencyNumber",
  "ExternalEmergencyNumber",
  "PlayerName",
  "Now",
  "CurrentLanguage",
  "UserEmail",
] as const;

export type BuiltInVariableName = (typeof BUILT_IN_VARIABLE_NAMES)[number];

const BUILT_IN_FRIENDLY_LABELS: Record<BuiltInVariableName, string> = {
  InternalEmergencyNumber: "Internal Emergency Number",
  ExternalEmergencyNumber: "External Emergency Number",
  PlayerName: "Player Name",
  Now: "Now",
  CurrentLanguage: "Current Language",
  UserEmail: "User Email",
};

function createBuiltInVariable(name: BuiltInVariableName): Variable {
  return {
    id: `GV_${name}`,
    name,
    type: VariableType.BuiltIn,
    value: "",
    scope: VariableScope.Public,
  };
}

export function getBuiltInSequenceVariables(): Variable[] {
  return BUILT_IN_VARIABLE_NAMES.map(createBuiltInVariable);
}

export function getActorVariableId(actorId: string): string {
  return `GV_ActorName_${actorId}`;
}

export function getActorDerivedSequenceVariables(actors: Actor[]): Variable[] {
  return actors.map((actor) => {
    const normalizedActor = normalizeActor(actor);

    return {
      id: getActorVariableId(normalizedActor.id),
      name: normalizedActor.workingName,
      type: VariableType.BuiltIn,
      value: "",
      scope: VariableScope.Public,
    };
  });
}

export function getReservedSequenceVariables(actors: Actor[] = []): Variable[] {
  return [
    ...getBuiltInSequenceVariables(),
    ...getActorDerivedSequenceVariables(actors),
  ];
}

export function isBuiltInVariableName(name: string): boolean {
  return BUILT_IN_VARIABLE_NAMES.includes(name as BuiltInVariableName);
}

export function isBuiltInVariableId(id: string): boolean {
  return (
    id.startsWith("GV_") &&
    isBuiltInVariableName(id.replace(/^GV_/, ""))
  );
}

export function isBuiltInVariable(variable: Variable): boolean {
  return isBuiltInVariableId(variable.id);
}

export function isActorDerivedVariableId(id: string): boolean {
  return id.startsWith("GV_ActorName_");
}

export function isReservedSequenceVariableId(
  id: string,
  actors: Actor[] = []
): boolean {
  return (
    isBuiltInVariableId(id) ||
    getActorDerivedSequenceVariables(actors).some((variable) => variable.id === id)
  );
}

export function isReservedSequenceVariableName(
  name: string,
  actors: Actor[] = []
): boolean {
  return (
    isBuiltInVariableName(name) ||
    getActorDerivedSequenceVariables(actors).some(
      (variable) => variable.name === name
    )
  );
}

export function getBuiltInFriendlyLabel(name: string): string {
  if (!isBuiltInVariableName(name)) {
    return name;
  }

  return BUILT_IN_FRIENDLY_LABELS[name as BuiltInVariableName];
}

function getUniqueCustomName(
  baseName: string,
  usedNames: Set<string>
): string {
  let candidate = `${baseName}_Custom`;
  let counter = 2;

  while (usedNames.has(candidate) || isBuiltInVariableName(candidate)) {
    candidate = `${baseName}_Custom_${counter}`;
    counter += 1;
  }

  return candidate;
}

export function normalizeSequenceVariables(
  variables: Variable[],
  actors: Actor[] = []
): Variable[] {
  const builtIns = getReservedSequenceVariables(actors);
  const usedIds = new Set<string>(builtIns.map((v) => v.id));
  const usedNames = new Set<string>(builtIns.map((v) => v.name));
  const normalized: Variable[] = [];

  variables.forEach((variable) => {
    if (!variable || typeof variable !== "object") return;

    const trimmedName =
      typeof variable.name === "string" ? variable.name.trim() : "";

    // Built-ins are always canonical and injected from this module at read-time.
    if (
      isReservedSequenceVariableId(variable.id, actors) ||
      isReservedSequenceVariableName(trimmedName, actors)
    ) {
      return;
    }

    const nextVariable: Variable = {
      ...variable,
      name: trimmedName || "Variable",
      type:
        variable.type === VariableType.BuiltIn
          ? VariableType.String
          : variable.type ?? VariableType.String,
      value: variable.value ?? "",
      scope: variable.scope ?? VariableScope.Public,
      id: variable.id || GetNewGuid(),
    };

    if (usedIds.has(nextVariable.id)) {
      nextVariable.id = GetNewGuid();
    }

    if (
      usedNames.has(nextVariable.name) ||
      isReservedSequenceVariableName(nextVariable.name, actors)
    ) {
      nextVariable.name = getUniqueCustomName(nextVariable.name, usedNames);
    }

    usedIds.add(nextVariable.id);
    usedNames.add(nextVariable.name);
    normalized.push(nextVariable);
  });

  return normalized;
}
